import type { Action, Button } from "@judo/model-api";

/**
 * Action definition types that require edit mode to be visible.
 * These buttons are only shown when the user has unsaved changes.
 */
const EDIT_MODE_ONLY_ACTION_TYPES = new Set(["UpdateActionDefinition", "CancelActionDefinition"]);

/**
 * Options for filtering visible buttons.
 */
export interface GetVisibleButtonsOptions {
	/** Whether the page is currently in edit mode (has unsaved changes) */
	isEditMode?: boolean;
}

/**
 * Filter buttons to only show those with matching page actions.
 * A button is visible if there's a page Action whose actionDefinition
 * references the same ActionDefinition as the button.
 *
 * Additional visibility rules:
 * - UpdateActionDefinition (Save) and CancelActionDefinition (Cancel) buttons
 *   are only visible when isEditMode=true.
 *
 * @param buttons - Array of buttons to filter
 * @param pageActions - Optional array of page actions to match against
 * @param options - Optional visibility options
 * @returns Filtered array of buttons that have matching page actions
 *
 * @example
 * ```typescript
 * const visibleButtons = getVisibleButtons(
 *   buttonGroup.buttons,
 *   page.actions,
 *   { isEditMode: true }
 * );
 * ```
 */
export function getVisibleButtons(
	buttons: Button[],
	pageActions?: Action[],
	options: GetVisibleButtonsOptions = {}
): Button[] {
	const { isEditMode = false } = options;

	if (!pageActions || pageActions.length === 0) {
		// No page actions - show all buttons (but still apply edit mode filter)
		return buttons.filter((button) => {
			const actionType = button.actionDefinition?.["@type"];
			const isEditModeAction = actionType != null && EDIT_MODE_ONLY_ACTION_TYPES.has(actionType);
			// In edit mode: only show edit-mode buttons (Save/Cancel)
			// Not in edit mode: show everything except edit-mode buttons
			return isEditMode ? isEditModeAction : !isEditModeAction;
		});
	}

	// Build set of actionDefinition xmi:ids from page actions
	const pageActionDefinitionIds = new Set<string>();
	for (const action of pageActions) {
		// actionDefinition should be resolved to an object
		const actionDef = action.actionDefinition;
		if (typeof actionDef === "object" && actionDef !== null) {
			const id = (actionDef as { "xmi:id"?: string })["xmi:id"];
			if (id) {
				pageActionDefinitionIds.add(id);
			}
		} else if (typeof actionDef === "string") {
			// Fallback: if still a string reference
			pageActionDefinitionIds.add(actionDef);
		}
	}

	// Filter buttons that have actionDefinitions matching page actions
	return buttons.filter((button) => {
		const buttonActionDef = button.actionDefinition;
		if (!buttonActionDef) return false;

		const buttonActionDefId = buttonActionDef["xmi:id"];
		if (!buttonActionDefId || !pageActionDefinitionIds.has(buttonActionDefId)) {
			return false;
		}

		// Check edit mode visibility
		const actionType = buttonActionDef["@type"];
		const isEditModeAction = actionType != null && EDIT_MODE_ONLY_ACTION_TYPES.has(actionType);
		// In edit mode: only show edit-mode buttons (Save/Cancel)
		// Not in edit mode: show everything except edit-mode buttons
		return isEditMode ? isEditModeAction : !isEditModeAction;
	});
}
